import { AddRowButton } from "@/components/ax/dataTable/AddRowButton";
import { AxDataTable } from "@/components/ax/dataTable/AxDataTable";
import { UpdateRowButton } from "@/components/ax/dataTable/UpdateRowButton";
import { PermissionChecker2 } from "@/components/permission/PermissionChecker2";
import { ROUTES } from "@/constants/routeConstants";
import { useAppNavigate } from "@/hooks/useAppNavigate";
import { useBreadcrumbs } from "@/hooks/useBreadcrumbs";
import { showSuccess, showApiError } from "@/lib/toast";
import { User } from "@/lib/types";
import { deleteUserSubAdmin, getUserSubAdmin } from "@/sideEffects/api";
import { selectActiveFirm } from "@/store/appSelectors";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { ColumnSort, ColumnDef } from "@tanstack/react-table";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

export const UserSubAdminPage = () => {
  const { t } = useTranslation();
  const activeFirm = useSelector(selectActiveFirm);
  const firmGuid = String(activeFirm?.firmGuid);

  const [sorting, setSorting] = useState<ColumnSort>();

  useBreadcrumbs([
    {
      name: t("entities.userSubAdmin"),
      path: ROUTES.USER_SUB_ADMIN,
    },
  ]);

  const { goToUserSubAdminDetail, goToUserSubAdminAdd } = useAppNavigate();

  const columns: ColumnDef<User>[] = [
    {
      accessorKey: "name",
      header: t("common.name"),
    },
    {
      accessorKey: "surname",
      header: t("user.surname"),
    },
    {
      accessorKey: "email",
      header: t("user.email"),
    },
    {
      accessorKey: "firms",
      header: t("entities.firm"),
    },
    {
      accessorKey: "rights",
      header: t("common.rights"),
    },
    {
      accessorKey: "id",
      header: () => "",
      cell: ({ row }) => {
        return (
          <PermissionChecker2 entity="UserSubAdmin" rights={["GetDetails"]}>
            <div className="flex items-center justify-end gap-2">
              <UpdateRowButton onClickUpdate={() => goToUserSubAdminDetail(String(row.original.id), firmGuid)} />
            </div>
          </PermissionChecker2>
        );
      },
    },
  ];

  const queryClient = useQueryClient();
  const deleteUsersMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      await Promise.all(ids.map((id) => deleteUserSubAdmin(id, firmGuid)));
    },
    onSuccess: () => {
      showSuccess(t("common.deletedSuccess"));
      queryClient.invalidateQueries({ queryKey: ["getUser"] });
    },
    onError: showApiError,
  });

  return (
    <div className="flex gap-4">
      <AxDataTable
        columns={columns}
        fetcher={getUserSubAdmin}
        uniqQueryKey="getUserSubAdmin"
        dynamicRequestParams={{
          sortBy: sorting?.id,
          sortDirection: sorting?.desc ? "desc" : "asc",
          firmGuid: firmGuid,
        }}
        getRowId={(row) => row.id.toString()}
        extraHeader={
          <PermissionChecker2 entity="UserSubAdmin" rights={["Add"]}>
            <AddRowButton
              title={t("common.addNewEntity", { entity: t("entities.user") })}
              onClickAdd={() => goToUserSubAdminAdd(firmGuid)}
            />
          </PermissionChecker2>
        }
        onDeleteRows={deleteUsersMutation.mutateAsync}
        deleting={deleteUsersMutation.isPending}
        sortableColumns={["name", "surname", "email"]}
        onClickSort={(sorting) => {
          if (sorting && sorting.length) {
            const sort = sorting[0];
            setSorting(sort);
          } else {
            setSorting(undefined);
          }
        }}
      />
    </div>
  );
};
